import React from 'react'
import {useTranslation} from 'react-i18next'

import {
    ContextualMenuItemType,
    DefaultButton,
    DetailsListLayoutMode,
    Dropdown,
    FontWeights,
    IColumn,
    IComboBoxOption,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
    Modal,
    PrimaryButton,
    SelectionMode,
    Separator,
    ShimmeredDetailsList,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react"
import {ApprovalListView, contentStyles, iconButtonStyles, ILookupValues} from "./"
import {useBranding} from "../../hooks/useBranding"
import ApprovalService, {
    IGetApprovalActionResponse,
    IGetApprovalListDetailRequest,
    IGetApprovalListDetailResponse,
    IGetApprovalListMasterResponse
} from "../../services/Approval"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage"
import {Controller, useFieldArray, useForm} from 'react-hook-form'
import { useState } from "react"
import { useEffect } from "react"
import {decode} from 'html-entities'



type Properties = {
    action: IGetApprovalActionResponse | null
    onDismissModal: (update: boolean, value?: string) => void
    showModal: boolean
    templateContentControls: IComboBoxOption[]
    masterLists: IGetApprovalListMasterResponse[]

}

export function ApprovalLookup({
                                   action,
                                   masterLists,
                                   onDismissModal,
                                   showModal,
                                   templateContentControls
                               }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()

    const [message, setMessage] = useState("")
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)
    const [isLoadedData, setIsLoadedData] = useState(false)
    const [isLoadingData, setIsLoadingData] = useState(true)
    const [emptyLists, setEmptyLists] = useState(true)
    const [lists, setLists] = useState<IComboBoxOption[]>([])
    const [validColumns, setValidColumns] = useState<IComboBoxOption[]>([])
    const [listDetails, setListDetails] = useState<IGetApprovalListDetailResponse[]>([])
    const [selectedList, setSelectedList] = useState<string | number | undefined>(undefined)
    const [selectedListComboBox, setSelectedListComboBox] = useState<IComboBoxOption | null>(null)

    const [showListView, setShowListView] = useState(false)


    interface ILookupColumn {
        index: number
        columnIndex: number
        validate: boolean
        columnName: string | undefined
        contentControl: string | undefined
    }

    type IFormLookupRule = {
        masterListId: number | undefined
        emailLookColumn: number | undefined
        lookupColumns: ILookupColumn[]
    }

    useEffect(() => {

        const lookupColumns = validColumns.map((option, index) => ({
            columnIndex: Number(option.index ?? -1),
            columnName: option.text,
            contentControl: undefined,
            index: index,
            validate: false
        }))

        lookupForm.reset({
            masterListId: lookupForm.getValues('masterListId'),
            emailLookColumn: lookupForm.getValues('emailLookColumn'),
            lookupColumns: lookupColumns
        })

        if (validColumns.length > 0) {
            if (action?.EmailAddressLookupRule) {
                try {
                    const lookupRuleArray = JSON.parse(action?.EmailAddressLookupRule) as ILookupValues[]
                    const currentEmailColumn = lookupRuleArray.find(item => item.Name.toLowerCase().trim() === "emailaddresscolumn")

                    if (currentEmailColumn) {

                        if (validColumns.some(col => String(col.key) === String(currentEmailColumn.Value))) {
                            lookupForm.setValue("emailLookColumn", Number(currentEmailColumn.Value))
                        }
                    }

                    const lookupColumnsArray = (JSON.parse(action?.EmailAddressLookupRule) as ILookupValues[]).filter(item => /^lookupcolumn\[[1-6]]map$/i.test(item.Name.toLowerCase().trim()))

                    lookupColumnsArray.forEach((item) => {

                        const match = item.Name.match(/^lookupcolumn\[(\d)]map$/i)
                        const index = match ? Number(match[1]) : undefined

                        if (index != undefined) {
                            try {
                                // @ts-ignore
                                lookupForm.setValue(`lookupColumns.${index - 1}.contentControl` as const, item.Value)
                                // @ts-ignore
                                lookupForm.setValue(`lookupColumns.${index - 1}.validate` as const, true)
                            } catch (_) {

                            }
                        }
                    })

                } catch (_) {

                }
            }
        }

    }, [validColumns])


    const lookupForm = useForm<IFormLookupRule>({
        defaultValues: {
            masterListId: undefined,
            emailLookColumn: undefined,
            lookupColumns: validColumns.map((option, index) => ({
                columnIndex: Number(option.key),
                columnName: option.text,
                contentControl: undefined,
                index: index
            }))
        }
        , mode: "onSubmit"
    })


    const lookupColumnsFieldArray = useFieldArray({
        control: lookupForm.control,
        name: "lookupColumns",
    })

    const lookupColumnWatchFieldArray = lookupForm.watch("lookupColumns")

    const lookupColumnsControlledFields = lookupColumnsFieldArray.fields.map((field, index) => {
        return {
            ...field,
            ...lookupColumnWatchFieldArray[index]
        }
    })

    /**
     *
     */
    useEffect(() => {
        if (showModal) {
            initLoad().then()

        }
    }, [showModal])


    const loadListDetail = async (masterListId: string) => {

        setMessage("")
        setShowMessage(false)
        setIsLoadingData(true)
        setListDetails([])
        setValidColumns([])
        lookupForm.reset()
        lookupForm.setValue("masterListId", Number(masterListId))

        const request: IGetApprovalListDetailRequest = {
            Mode: 'approvalListMasterId',
            Id: masterListId
        }

        await ApprovalService.getApprovalListDetail(request)
            .then(data => {
                if (data.Success) {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalListDetailResponse>
                    if (Array.isArray(resultData)) {
                        setListDetails(resultData)

                        if (resultData.length === 0) return []

                        const columnNames = ['Column1', 'Column2', 'Column3', 'Column4', 'Column5', 'Column6']
                        const validColumnsSet = new Map<number, string>()


                        resultData.forEach((item, index) => {
                            columnNames.forEach((col, index) => {
                                if ((item as any)[col] != null) {
                                    validColumnsSet.set(index, col)
                                }
                            })
                        })

                        const validColumns: IComboBoxOption[] = Array.from(validColumnsSet.entries()).map(([index, col]) => ({
                            key: index + 1,
                            text: col
                        }))

                        setValidColumns(validColumns.sort((a, b) => String(a.text).localeCompare(String(b.text))))
                    } else {
                        showStatusMessage(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } else {
                    showStatusMessage(t('message.error', {
                        ns: 'common',
                        Error: data.ExceptionMessage
                    }), StatusEnum.Error)
                }
            })
            .catch((error) => {
                showStatusMessage(t('message.error', {ns: 'common', Error: error.message}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoadingData(false)
            })

    }


    /**
     * Load init
     */
    const initLoad = async () => {
        setMessage("")
        setShowMessage(false)
        setIsLoadingData(true)
        setIsLoadedData(false)
        setSelectedList(undefined)
        setLists([])
        lookupForm.clearErrors()
        lookupForm.reset()


        if (masterLists.length > 0) {

            setEmptyLists(false)

            setLists(masterLists.map(item => ({
                key: item.Id,
                text: item.Name
            })))

            if (action?.EmailAddressLookupRule) {
                try {
                    const lookupRuleArray = JSON.parse(action?.EmailAddressLookupRule) as ILookupValues[]
                    const currentList = lookupRuleArray.find(item => item.Name.toLowerCase().trim() === "looklist")

                    if (currentList) {
                        lookupForm.setValue("masterListId", Number(currentList.Value))
                        setSelectedList(currentList.Value)
                        loadListDetail(currentList.Value).then()
                    }

                } catch (_) {

                }
            }
        }

        setIsLoadingData(false)
        setIsLoadedData(true)
    }


    /**
     * show Status Message
     * @param message
     * @param status
     */
    const showStatusMessage = (message: string, status: StatusEnum) => {
        setMessage(decode(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * On Before dismiss
     */
    const onBeforeDismiss = () => {
        onDismissModal(false, undefined)
    }


    const menuApprovalStepsProps = (): IContextualMenuProps => {

        const menuContexItems: IContextualMenuItem[] = []

        if (!emptyLists) {
            menuContexItems.push({
                    key: 'view',
                    text: t("text.viewList", {ns: "approval"}),
                    title: t("text.viewList", {ns: "approval"}),
                    iconProps: {iconName: 'RedEye'},
                    onClick: () => {
                        ShowListView()
                    }
                }
            )

            menuContexItems.push(
                {
                    key: 'Divider1',
                    itemType: ContextualMenuItemType.Divider
                }
            )
        }

        menuContexItems.push(
            {
                key: 'edit',
                text: t("text.adminLists", {ns: "approval"}),
                title: t("text.adminLists", {ns: "approval"}),
                iconProps: {iconName: 'Edit'},
                onClick: () => {

                }
            }
        )

        return {
            items: menuContexItems,
            directionalHintFixed: true,
        }
    }


    const contentControlColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.column', {ns: 'approval'}),
            fieldName: 'columnName',
            minWidth: 80,
            maxWidth: 80,
            isMultiline: false,
            isResizable: false,
            onRender: (item: ILookupColumn) => {
                return <>
                    <Stack styles={{root: {justifyContent: 'center', height: '100%'}}}>
                        <Text variant={"medium"} styles={{root: {fontWeight: FontWeights.semibold}}}>
                            {item.columnName}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column2',
            name: '',
            fieldName: '',
            minWidth: 32,
            maxWidth: 32,
            isMultiline: false,
            isResizable: false,
            onRender: (item: ILookupColumn) => {
                return <>
                    <Stack styles={{root: {textOverflow: 'none'}}}>
                        {
                            !item.validate &&
                            <>
                                <DefaultButton
                                    iconProps={{iconName: 'Add'}}
                                    styles={{
                                        root: {
                                            width: 32,
                                            minWidth: 32,
                                            maxWidth: 32
                                        }
                                    }}
                                    title={t('Common.Button.AddMapping', {ns: 'common'})}
                                    onClick={() => {

                                        lookupColumnsFieldArray.update(item.index, {
                                            ...item,
                                            validate: true
                                        })

                                    }}
                                />
                            </>
                        }
                    </Stack>
                </>
            }
        },
        {
            key: 'column3',
            name: t('title.contentControls', {ns: 'approval'}),
            fieldName: '',
            minWidth: 100,
            isMultiline: false,
            isResizable: true,
            onRender: (item: ILookupColumn) => {
                return <>
                    <Stack styles={{root: {textOverflow: 'none'}}}>

                        {item.validate &&
                            <Controller
                                name={`lookupColumns.${item.index}.contentControl` as const}
                                rules={{required: item.validate ? t('text.fieldRequired', {ns: 'common'}) : false}}
                                control={lookupForm.control}
                                render={({field, fieldState: {error}}) =>
                                    <>
                                        <Dropdown selectedKey={field.value}
                                                  {...field}
                                                  required={true}
                                                  onChange={(_, option) => {
                                                      field.onChange(option?.key)
                                                  }}
                                                  errorMessage={error && error.message}
                                                  options={templateContentControls}
                                                  disabled={isLoadingData}
                                                  title={t('text.selectOption', {ns: 'common'})}
                                        />
                                    </>
                                }
                            />
                        }
                    </Stack>
                </>
            }

        },
        {
            key: 'column4',
            name: '',
            minWidth: 32,
            maxWidth: 32,
            isMultiline: false,
            isResizable: false,
            onRender: (item: ILookupColumn) => {
                return <>
                    <Stack styles={{root: {textOverflow: 'none'}}}>
                        {
                            item.validate &&
                            <>
                                <DefaultButton
                                    iconProps={{iconName: 'Delete'}}
                                    styles={{
                                        root: {
                                            width: 32,
                                            minWidth: 32,
                                            maxWidth: 32
                                        }
                                    }}
                                    title={t('Common.Button.DeleteMapping', {ns: 'common'})}
                                    onClick={() => {

                                        lookupColumnsFieldArray.update(item.index, {
                                            ...item,
                                            contentControl: undefined,
                                            validate: false
                                        })

                                    }}
                                />
                            </>
                        }
                    </Stack>
                </>
            }
        }
    ]


    const apply = async () => {
        lookupForm.clearErrors()
        await lookupForm.handleSubmit((data) => {

                const lookupRuleArray = [
                    {
                        Name: "LookList",
                        Value: String(data.masterListId)
                    },
                    {
                        Name: "EmailAddressColumn",
                        Value: String(data.emailLookColumn ?? 0)
                    }]

                data.lookupColumns.forEach(column => {
                    if (column.validate) {
                        lookupRuleArray.push(
                            {
                                Name: `LookupColumn[${column.index + 1}]Map`,
                                Value: column.contentControl ?? ''
                            }
                        )
                    }
                })
                onDismissModal(true, JSON.stringify(lookupRuleArray))

            }, (_) => {

            }
        )()
    }

    function ShowListView() {
        const selectedListItem = lists.find(item => item.key === Number(selectedList))

        if (selectedListItem) {
            setSelectedListComboBox(selectedListItem)
            setShowListView(true)
        }
    }

    /**
     * Closes the list view by setting its visibility state to false.
     * Typically used to hide the list view component from the user interface.
     */
    const closeListView = () => {
        setShowListView(false)

    }


    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'delete',
                text: t('Common.Button.Remove', {ns: 'common'}),
                iconProps: {iconName: 'Trash'},
                onClick: () => {
                    lookupForm.clearErrors()
                    onDismissModal(true, '')
                }
            },
        ],
    }

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerRecordPreview}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"}
                                  title={t('title.lookupRule', {ns: 'approval'})}>
                                {t('title.lookupRule', {ns: 'approval'})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onBeforeDismiss}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">


                                    {showMessage &&
                                        <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                       truncated={false}
                                                       isMultiline={true}
                                                       dismiss={false}
                                                       setShowMessage={showMessage}>
                                            <div>{message}</div>
                                        </StatusMessage>
                                    }

                                    <Stack horizontal tokens={{childrenGap: 8}}>

                                        <Stack grow={1}>
                                            <Controller name="masterListId" control={lookupForm.control}

                                                        rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                        render={({
                                                                     field,
                                                                     fieldState: {error}
                                                                 }) =>
                                                            <Dropdown selectedKey={field.value}
                                                                      {...field}
                                                                      required={true}
                                                                      onChange={(_, option) => {
                                                                          field.onChange(option?.key)
                                                                          setSelectedList(option?.key)
                                                                          if (option?.key !== undefined) {
                                                                              loadListDetail(String(option?.key)).then()
                                                                          }
                                                                      }}
                                                                      errorMessage={error && error.message}
                                                                      options={lists}
                                                                      label={t('text.lists', {ns: 'approval'})}
                                                                      disabled={isLoadingData}
                                                                      title={t('text.selectOption', {ns: 'common'})}

                                                            />}
                                            />
                                        </Stack>
                                        <Stack styles={{root: {paddingTop: 28}}}>

                                            <IconButton iconProps={{iconName: 'redEye'}}
                                                        menuIconProps={{
                                                            style: {display: 'none'},
                                                        }}
                                                        onClick={ShowListView}
                                                        title={t('label.colShowActions', {ns: 'common'})}
                                                        ariaLabel={t('label.colShowActions', {ns: 'common'})}
                                                        disabled={isLoadingData}/>

                                        </Stack>
                                    </Stack>

                                    <Separator/>

                                    <Controller name="emailLookColumn" control={lookupForm.control}

                                                rules={{required: t('text.fieldRequired', {ns: 'common'})}}
                                                render={({
                                                             field,
                                                             fieldState: {error}
                                                         }) =>
                                                    <Dropdown selectedKey={field.value}
                                                              {...field}
                                                              required={true}
                                                              onChange={(_, option) => {
                                                                  field.onChange(option?.key)
                                                              }}
                                                              errorMessage={error && error.message}
                                                              options={validColumns}
                                                              label={t('text.emailAddressesColumn', {ns: 'approval'})}
                                                              disabled={isLoadingData}
                                                              title={t('text.selectOption', {ns: 'common'})}

                                                    />}
                                    />


                                    <ShimmeredDetailsList
                                        items={lookupColumnsControlledFields}
                                        enableShimmer={isLoadingData}
                                        compact={false}
                                        columns={contentControlColumns}
                                        selectionMode={SelectionMode.none}
                                        layoutMode={DetailsListLayoutMode.justified}
                                        isHeaderVisible={true}
                                        isSelectedOnFocus={false}
                                        setKey={"Id"}
                                    />

                                    {lookupColumnsControlledFields.length === 0 &&
                                        <Text variant={"small"}
                                              styles={{root: {fontStyle: 'italic'}}}>{t('label.noItems', {ns: 'common'})}</Text>
                                    }

                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>


                                    {isLoadingData &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                         label={t('text.spinner', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    <Stack>
                                        <PrimaryButton disabled={!isLoadedData || isLoadingData}
                                                       style={{marginLeft: 8}}
                                                       split
                                                       menuProps={menuProps}
                                                       onClick={() => {
                                                           apply().then()
                                                       }}
                                                       text={t('Common.Button.Apply', {ns: 'common'})}
                                                       title={t('Common.Button.Apply', {ns: 'common'})}/>
                                    </Stack>

                                    <DefaultButton
                                        onClick={() => {
                                            onBeforeDismiss()
                                        }}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>

            {showListView &&
                <ApprovalListView
                    listDetails={listDetails}
                    showModal={showListView}
                    list={selectedListComboBox}
                    onDismissModal={closeListView}
                />
            }
        </>
    )
}


